// src/app/pages/prescription/prescription-list/prescription-line/prescription-line.component.ts
import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatPrefix, MatSuffix } from '@angular/material/form-field';
import { MatOption, MatSelect } from '@angular/material/select';
import { NgForOf, NgIf } from '@angular/common';
import { MatButton, MatIconButton } from '@angular/material/button';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { PrescriptionLine } from '@core/models/PrescriptionLine';
import { UppercaseNoAccentDirective } from '@shared/directives/uppercase-no-accent.directive';

type FormErrors = {
  dose: boolean;
  unitId: boolean;
  frecuency: boolean;
  frecuencyType: boolean;
  duration: boolean;
  durationType: boolean;
  route: boolean;
};

@Component({
  selector: 'app-prescription-line',
  standalone: true,
  imports: [
    FormsModule,
    MatButton,
    MatFormField,
    MatIcon,
    MatInput,
    MatLabel,
    MatOption,
    MatSelect,
    NgForOf,
    ReactiveFormsModule,
    MatIconButton,
    MatDialogContent,
    MatPrefix,
    MatSuffix,
    UppercaseNoAccentDirective,
    NgIf,
  ],
  templateUrl: './prescription-line.component.html',
  styleUrl: './prescription-line.component.scss',
})
export class PrescriptionLineComponent {
  // Modelo
  dose: number;
  unitId: number;
  duration: number;
  durationType: string;
  route: number;
  indication: number = 0;
  name: string;
  summary: string;
  frecuency: string | number;
  obs: string;
  frecuencyTypes: any[] = [];
  frecuencyType: any = null;
  idDosisUnica = 1;

  // Filtros y listas
  showSearch = { units: false, routes: false, indications: false };
  filters = { units: '', routes: '', indications: '' };
  original = { units: [] as any[], routes: [] as any[], indications: [] as any[] };
  filtered = { units: [] as any[], routes: [] as any[], indications: [] as any[] };
  convertedRoutes: any[] = [];

  // Validacion
  formErrors: FormErrors = {
    dose: false,
    unitId: false,
    frecuency: false,
    frecuencyType: false,
    duration: false,
    durationType: false,
    route: false,
  };
  missingFields: string[] = [];
  attemptedSave = false;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      frecuencyTypes: any[];
      routes: any[];
      indications: any[];
      units: any[];
      name: string;
      summary: string;
      toUpdate: PrescriptionLine;
      isParapharmacy?: boolean;
    },
    private dialogRef: MatDialogRef<PrescriptionLineComponent>
  ) {
    if (data.routes) this.convertedRoutes = [...data.routes];

    // Init
    this.name = data.name;
    this.summary = data.summary;
    this.frecuencyTypes = data.frecuencyTypes;

    const { toUpdate } = data;
    this.dose = toUpdate.dose || 0;
    this.unitId = toUpdate.unitId || 0;
    this.duration = toUpdate.duration || 0;
    this.durationType = toUpdate.durationType || '';
    this.route = toUpdate.route || 0;
    this.indication = Number((toUpdate as any).indication ?? 0);
    this.name = toUpdate.name || this.name;
    this.summary = toUpdate.summary || this.summary;
    this.frecuency = toUpdate.frecuency ?? '';
    this.frecuencyType = toUpdate.idFrecuencyType ?? 0;
    this.obs = toUpdate.obs || '';

    // Copias para filtros
    this.original.units = [...(data.units ?? [])];
    this.original.routes = [...(this.convertedRoutes ?? [])];
    this.original.indications = [...(data.indications ?? [])];

    this.showSearch.units = this.original.units.length > 10;
    this.showSearch.routes = this.original.routes.length > 10;
    this.showSearch.indications = this.original.indications.length > 10;

    this.resetFiltered('units');
    this.resetFiltered('routes');
    this.resetFiltered('indications');

    if (this.frecuencyType === this.idDosisUnica) this.frecuency = 1;
  }

  // ---------- Filtros ----------
  onSelectOpened(kind: 'units' | 'routes' | 'indications') {
    if (!this.filters[kind]) this.resetFiltered(kind);
  }
  clearFilter(kind: 'units' | 'routes' | 'indications', inputEl?: HTMLInputElement) {
    this.filters[kind] = '';
    this.resetFiltered(kind);
    if (inputEl) setTimeout(() => inputEl.focus(), 0);
  }
  applyFilter(kind: 'units' | 'routes' | 'indications') {
    const term = (this.filters[kind] || '').toString().trim().toLowerCase();
    if (!term) {
      this.resetFiltered(kind);
      this.revalidateIfNeeded();
      return;
    }
    if (kind === 'units') {
      this.filtered.units = this.original.units.filter((u) => this.normalize(u?.name).includes(term));
    } else if (kind === 'routes') {
      this.filtered.routes = this.original.routes.filter((r) => this.normalize(r?.name).includes(term));
    } else {
      this.filtered.indications = this.original.indications.filter((i) => this.normalize(i?.name).includes(term));
    }
  }
  private resetFiltered(kind: 'units' | 'routes' | 'indications') {
    if (kind === 'units') this.filtered.units = [...this.original.units];
    else if (kind === 'routes') this.filtered.routes = [...this.original.routes];
    else this.filtered.indications = [...this.original.indications];
  }
  private normalize(v: string | null | undefined): string {
    return (v ?? '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  // trackBy
  trackByUnit = (_: number, item: any) => item?.idUnit ?? item;
  trackByRoute = (_: number, item: any) => item?.idRoute ?? item;
  trackByIndication = (_: number, item: any) => item?.id ?? item;

  // ---------- Helpers ----------
  onChangeNumber(prop: 'dose'|'frecuency'|'duration', value: number | string) {
    const n = Number(value);
    (this as any)[prop] = Number.isFinite(n) ? Math.max(0, n) : 0;
  }
  onFrecuencyTypeChange(val: number) {
    if (val === this.idDosisUnica) this.frecuency = 1;
  }
  revalidateIfNeeded() {
    if (this.attemptedSave) this.validateForm(); // actualiza banner y missingFields en vivo
  }
  cancel(): void {
    this.dialogRef.close(null);
  }

  // ---------- Validacion ----------
  private validateForm(): boolean {
    // reset
    this.formErrors = {
      dose: false,
      unitId: false,
      frecuency: false,
      frecuencyType: false,
      duration: false,
      durationType: false,
      route: false,
    };
    this.missingFields = [];

    if (!(Number(this.dose) > 0)) { this.formErrors.dose = true; this.missingFields.push('CANTIDAD'); }
    if ((this.data.units?.length ?? 0) > 0 && !this.unitId) { this.formErrors.unitId = true; this.missingFields.push('UNIDAD'); }
    if (!this.frecuencyType) { this.formErrors.frecuencyType = true; this.missingFields.push('TIPO DE FRECUENCIA'); }
    if (this.frecuencyType !== this.idDosisUnica && !(Number(this.frecuency) > 0)) {
      this.formErrors.frecuency = true; this.missingFields.push('FRECUENCIA');
    }
    if (!(Number(this.duration) > 0)) { this.formErrors.duration = true; this.missingFields.push('DURACION'); }
    if (!this.durationType) { this.formErrors.durationType = true; this.missingFields.push('PERIODO'); }
    if ((this.data.routes?.length ?? 0) > 0 && !this.route) { this.formErrors.route = true; this.missingFields.push('VIA DE ADMINISTRACION'); }

    return this.missingFields.length === 0;
  }

  save(): void {
    this.attemptedSave = true;

    const ok = this.validateForm();
    if (!ok) {
      // primer intento: enfoca banner
      setTimeout(() => document.getElementById('poso-banner')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
      return;
    }

    const indicationId = Number(this.indication ?? 0);

    const indicationName =
      (this.data.indications?.length ?? 0) > 0
        ? this.data.indications.find((i) => Number(i.id) === indicationId)?.name ?? null
        : null;


    const durationName = this.optionsDuration.find((opt) => opt.value === this.durationType)?.label;
    const unitIdName =
      this.data.units.length > 0 ? this.data.units.find((unit) => unit.idUnit === this.unitId)?.name : null;

    const routeObj = this.convertedRoutes.find((route) => route.idRoute === this.route);
    const routeName = routeObj?.name ?? 0;

    const selectedFreq = this.frecuencyTypes.find((f) => f.idFrecuencyType === this.frecuencyType);

    this.dialogRef.close({
      dose: this.dose,
      unitId: this.unitId ? this.unitId : 0,
      duration: this.duration,
      durationType: this.durationType,
      route: this.route,
      indication: indicationId,
      indicationName,
      frecuency: this.frecuency,
      frecuencyType: selectedFreq?.frecuencyType || '',
      idFrecuencyType: selectedFreq?.idFrecuencyType || 0,
      obs: this.obs,
      durationName,
      unitIdName,
      routeName,
    });
  }

  // Etiquetas de duracion
  optionsDuration = [
    { value: 'YEAR', label: 'AÑO(S)' },
    { value: 'MONTH', label: 'MES(ES)' },
    { value: 'WEEK', label: 'SEMANA(S)' },
    { value: 'DAY', label: 'DIA(S)' },
    { value: 'HOUR', label: 'HORA(S)' },
    { value: 'MINUTE', label: 'MINUTO(S)' },
  ];
}
